import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Button } from '@/components/ui/Button'
import { NewsletterStatusPill } from '@/components/ui/NewsletterStatusPill'
import { ApiError } from '@/lib/api'
import { getAudienceCounts, listCampaigns } from '@/lib/newsletter'
import type { NewsletterCampaign } from '@/lib/newsletter-types'

function relativeTime(iso: string | null): string {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return '—'
  const diffSec = Math.round((t - Date.now()) / 1000)
  const abs = Math.abs(diffSec)
  const fmt = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  if (abs < 60) return fmt.format(diffSec, 'second')
  if (abs < 3600) return fmt.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400) return fmt.format(Math.round(diffSec / 3600), 'hour')
  return fmt.format(Math.round(diffSec / 86400), 'day')
}

function KpiTile({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs text-[var(--content-tertiary)] uppercase tracking-wider mb-1">{label}</p>
        <p className="text-xl font-heading font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) {
      return 'Your session may have expired. Please sign in again.'
    }
    if (err.status === 404) {
      return fallback
    }
    if (err.status >= 500) {
      return 'The server returned an error. Please try again in a moment.'
    }
    if (err.status >= 400) {
      return 'The request was rejected by the server. Please try again.'
    }
    return fallback
  }
  if (err instanceof Error) {
    if (err.name === 'TypeError') {
      return 'We could not reach the server. Check your connection and try again.'
    }
    return fallback
  }
  return fallback
}

export function NewsletterListPage() {
  const audienceQ = useQuery({
    queryKey: ['admin-newsletter-audience'],
    queryFn: getAudienceCounts,
  })

  const campaignsQ = useQuery({
    queryKey: ['admin-newsletter-campaigns'],
    queryFn: () => listCampaigns({ limit: 100 }),
  })

  const items: NewsletterCampaign[] = campaignsQ.data?.items ?? []

  const drafts = items.filter((c) => c.status === 'draft').length
  const scheduled = items.filter((c) => c.status === 'scheduled').length
  const sending = items.filter((c) => c.status === 'sending' || c.status === 'failed').length
  const sent = items.filter((c) => c.status === 'sent').length

  const audienceFree = audienceQ.data?.free?.toLocaleString() ?? '—'
  const audiencePaid = audienceQ.data?.paid?.toLocaleString() ?? '—'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Newsletters</h1>
          <p className="text-sm text-[var(--content-tertiary)] mt-1">
            Design, schedule, and send broadcast emails via Postmark.
          </p>
        </div>
        <Link to="/newsletters/new">
          <Button>New campaign</Button>
        </Link>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KpiTile label="Audience (free)" value={audienceFree} />
          <KpiTile label="Audience (paid)" value={audiencePaid} />
          <KpiTile label="Drafts" value={drafts.toLocaleString()} />
          <KpiTile label="Scheduled" value={scheduled.toLocaleString()} />
          <KpiTile label="Sending / failed" value={sending.toLocaleString()} />
        </div>
        {audienceQ.isError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex flex-wrap items-center gap-3">
            <span>
              <span className="font-medium">Couldn&rsquo;t load audience counts.</span>{' '}
              {describeError(audienceQ.error, 'Audience data is unavailable.')}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => audienceQ.refetch()}
              disabled={audienceQ.isFetching}
            >
              {audienceQ.isFetching ? 'Retrying…' : 'Retry'}
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <h2 className="font-heading font-semibold text-sm">Campaigns</h2>
        </CardHeader>
        <CardContent>
          {campaignsQ.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : campaignsQ.isError ? (
            <ErrorState
              title="Could not load campaigns"
              description={describeError(campaignsQ.error, 'Something went wrong while loading campaigns.')}
              action={
                <Button onClick={() => campaignsQ.refetch()} disabled={campaignsQ.isFetching}>
                  {campaignsQ.isFetching ? 'Retrying…' : 'Try again'}
                </Button>
              }
            />
          ) : items.length === 0 ? (
            <EmptyState
              title="No campaigns yet"
              description="Create your first newsletter to start building the free and paid broadcast lists."
              action={
                <Link to="/newsletters/new">
                  <Button>Create campaign</Button>
                </Link>
              }
            />
          ) : (
            <div className="overflow-x-auto -mx-4">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-[var(--content-tertiary)]">
                  <tr>
                    <th className="px-4 py-2 font-medium">Subject</th>
                    <th className="px-4 py-2 font-medium">Audience</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Scheduled / Sent</th>
                    <th className="px-4 py-2 font-medium">Recipients</th>
                    <th className="px-4 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} className="border-t border-[var(--border)] hover:bg-[var(--surface-nested)]/40">
                      <td className="px-4 py-3 max-w-[420px]">
                        <Link to={`/newsletters/${c.id}`} className="font-medium text-[var(--content)] hover:text-[var(--brand)] line-clamp-1">
                          {c.subject || <span className="text-[var(--content-tertiary)] italic">(untitled)</span>}
                        </Link>
                        <p className="text-xs text-[var(--content-tertiary)] mt-0.5">
                          Updated {relativeTime(c.updated_at)}
                        </p>
                      </td>
                      <td className="px-4 py-3 capitalize">
                        {c.audience === 'both' ? 'Free + Paid' : c.audience}
                      </td>
                      <td className="px-4 py-3">
                        <NewsletterStatusPill status={c.status} />
                      </td>
                      <td className="px-4 py-3 text-[var(--content-secondary)] tabular-nums">
                        {c.status === 'sent' || c.status === 'sending'
                          ? relativeTime(c.sent_at)
                          : c.scheduled_for
                            ? relativeTime(c.scheduled_for)
                            : '—'}
                      </td>
                      <td className="px-4 py-3 tabular-nums">{c.total_recipients?.toLocaleString() ?? '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/newsletters/${c.id}`}
                          className="text-sm font-medium text-[var(--brand)] hover:underline"
                        >
                          Open →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!campaignsQ.isError && (
            <p className="mt-3 text-xs text-[var(--content-tertiary)]">
              Sent <span className="font-semibold text-emerald-600">{sent}</span> campaigns total
              {' '}· Auditor data older than {items.length} items not shown.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
